import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Popover,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import { tokens } from "../../../theme";
import {
  ALL_STATE_CODES,
  CompoundType,
  CoverageEntry,
  EMPTY_COVERAGE,
  FIPS_TO_CODE,
  Pharmacy,
  STATE_NAMES,
  TOTAL_JURISDICTIONS,
  coverageByState,
  loadCoverage,
  pharmacyStateCount,
  resolvePharmacyId,
} from "./coverageData";

const US_TOPO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const BRAND_GRADIENT = "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)";

const MAP_WIDTH = 975;
const MAP_HEIGHT = 610;
const DEFAULT_MAP_POSITION = { coordinates: [-96, 38] as [number, number], zoom: 1 };
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;

type SearchOption =
  | { kind: "state"; code: string; label: string }
  | { kind: "pharmacy"; id: string; label: string };

export interface PharmacyCoverageNavState {
  compoundType: CompoundType;
  pharmacyFilter: string;
  /** When true, lock UI to this product's manufacturer + compound type. */
  productScoped?: boolean;
}

export default function PharmacyShipToCoverage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const colors = tokens(theme.palette.mode);
  const location = useLocation();

  const navState = location.state as PharmacyCoverageNavState | null;
  if (process.env.NODE_ENV !== "production" && !navState) {
    // eslint-disable-next-line no-console
    console.warn(
      "PharmacyShipToCoverage was opened without required navigation state (compoundType, pharmacyFilter). Falling back to defaults.",
    );
  }
  const initialCompoundType: CompoundType = navState?.compoundType === "nonsterile" ? "nonsterile" : "sterile";
  const requestedFilter = navState?.pharmacyFilter && navState.pharmacyFilter !== "all"
    ? navState.pharmacyFilter
    : "all";
  const isProductScoped = Boolean(navState?.productScoped) && requestedFilter !== "all";

  const [compoundType, setCompoundType] = useState<CompoundType>(initialCompoundType);
  const [filter, setFilter] = useState<string>(requestedFilter);
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);
  const [coverage, setCoverage] = useState<Record<string, CoverageEntry>>(EMPTY_COVERAGE);
  const [coverageLoading, setCoverageLoading] = useState(true);
  const [coverageError, setCoverageError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [pointer, setPointer] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [detailAnchor, setDetailAnchor] = useState<{ x: number; y: number } | null>(null);
  const [infoAnchor, setInfoAnchor] = useState<HTMLElement | null>(null);
  const [mapPosition, setMapPosition] = useState(DEFAULT_MAP_POSITION);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const zoomIn = () => setMapPosition((pos) => ({ ...pos, zoom: Math.min(pos.zoom * 1.5, MAX_ZOOM) }));
  const zoomOut = () => setMapPosition((pos) => ({ ...pos, zoom: Math.max(pos.zoom / 1.5, MIN_ZOOM) }));
  const resetZoom = () => setMapPosition(DEFAULT_MAP_POSITION);

  useEffect(() => {
    let cancelled = false;
    setCoverageLoading(true);
    setCoverageError(null);
    loadCoverage()
      .then((data) => {
        if (cancelled) return;
        const pharmaciesOnRoster = data.pharmacies;
        const resolvedFilter =
          requestedFilter === "all"
            ? "all"
            : pharmaciesOnRoster.some((p) => p.id === requestedFilter)
              ? requestedFilter
              : resolvePharmacyId(requestedFilter, undefined, pharmaciesOnRoster);
        const scopedPharmacies =
          isProductScoped && resolvedFilter !== "all"
            ? pharmaciesOnRoster.filter((p) => p.id === resolvedFilter)
            : pharmaciesOnRoster;
        setFilter(resolvedFilter);
        setPharmacies(scopedPharmacies);
        setCoverage(data.coverage);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("Failed to load pharmacy license coverage", err);
        setCoverageError("Unable to load pharmacy license coverage.");
        setCoverage(EMPTY_COVERAGE);
      })
      .finally(() => {
        if (!cancelled) setCoverageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen();
    }
  };

  const exportMapImage = () => {
    const svg = containerRef.current?.querySelector("svg");
    if (!svg) return;
    const svgString = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const scale = 2;
      const canvas = document.createElement("canvas");
      canvas.width = MAP_WIDTH * scale;
      canvas.height = MAP_HEIGHT * scale;
      const ctx = canvas.getContext("2d");
      URL.revokeObjectURL(url);
      if (!ctx) return;
      ctx.fillStyle = isDark ? "#0f1721" : "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `pharmacy-ship-to-coverage-${compoundType}.png`;
        link.click();
        URL.revokeObjectURL(link.href);
      });
    };
    img.src = url;
  };

  const pharmacyColor = (id: string) => {
    const ph = pharmacies.find((p) => p.id === id);
    if (ph?.color) return ph.color;
    return colors.warning[300];
  };

  const coveredFill = "#0f766e";
  const badgeColor = colors.warning[300];

  const byState = useMemo(
    () => coverageByState(compoundType, filter, pharmacies, coverage),
    [compoundType, filter, pharmacies, coverage],
  );
  const overviewPharmacies = useMemo(
    () => (filter === "all" ? pharmacies : pharmacies.filter((p) => p.id === filter)),
    [filter, pharmacies],
  );
  const coveredCount = useMemo(
    () => Object.keys(coverageByState(compoundType, filter, pharmacies, coverage)).length,
    [compoundType, filter, pharmacies, coverage],
  );
  const typeLabel = compoundType === "sterile" ? "Sterile" : "Non-sterile";
  const isNonSterileComingSoon = !isProductScoped && compoundType === "nonsterile";

  const uncoveredFill = isDark ? colors.grey[700] : colors.grey[700];
  const strokeColor = theme.palette.background.paper;
  const selectedStroke = colors.warning[300];
  const hoverFill = colors.primary[200];

  const fillFor = (code: string) => ((byState[code]?.length ?? 0) > 0 ? coveredFill : uncoveredFill);

  const selectedPartners = selected ? byState[selected] ?? [] : [];

  const searchOptions: SearchOption[] = useMemo(
    () => [
      ...ALL_STATE_CODES.map((code) => ({ kind: "state" as const, code, label: STATE_NAMES[code] })),
      ...(isProductScoped
        ? []
        : pharmacies.map((p) => ({ kind: "pharmacy" as const, id: p.id, label: p.name }))),
    ],
    [pharmacies, isProductScoped],
  );

  const selectedStateOption = useMemo(
    () =>
      selected
        ? searchOptions.find((opt) => opt.kind === "state" && opt.code === selected) ?? null
        : null,
    [selected, searchOptions],
  );

  const clearSelectedState = () => {
    setSelected(null);
    setDetailAnchor(null);
  };

  const handleStateClick = (code: string, evt: React.MouseEvent) => {
    setSelected(code);
    setDetailAnchor({ x: evt.clientX, y: evt.clientY });
  };

  const cardSx = {
    borderRadius: 4,
    border: 1,
    borderColor: alpha("#0d9488", isDark ? 0.28 : 0.16),
    bgcolor: alpha("#0d9488", isDark ? 0.08 : 0.05),
    backdropFilter: "blur(10px)",
    boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.28)" : "0 1px 3px rgba(15,23,42,0.06)",
  };

  return (
    <Box m={2}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: BRAND_GRADIENT,
            boxShadow: "0 6px 16px rgba(13,148,136,0.3)",
            flexShrink: 0,
          }}
        >
          <LocalShippingOutlinedIcon sx={{ color: "#fff" }} />
        </Box>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: -0.4 }}>
            Pharmacy Ship-To Coverage
          </Typography>
          <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: 400, mt: 0.25 }}>
            See which pharmacy partners can ship to each state, by compound type.
          </Typography>
        </Box>
      </Stack>

      <Paper elevation={0} sx={{ ...cardSx, p: 1.5, mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={compoundType}
            onChange={(_, v: CompoundType | null) => {
              if (isProductScoped) return;
              if (v) setCompoundType(v);
            }}
            sx={{
              bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)",
              borderRadius: 999,
              p: 0.4,
              "& .MuiToggleButton-root": { border: "none", borderRadius: "999px !important", px: 2 },
              "& .MuiToggleButton-root.Mui-selected": {
                background: BRAND_GRADIENT,
                color: "#fff",
                boxShadow: "0 2px 8px rgba(13,148,136,0.35)",
                "&:hover": { background: "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)" },
              },
            }}
          >
            {(compoundType === "sterile" || !isProductScoped) && (
              <ToggleButton value="sterile">
                <ScienceOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                Sterile
              </ToggleButton>
            )}
            {(compoundType === "nonsterile" || !isProductScoped) && (
              <ToggleButton value="nonsterile">
                <MedicationOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                Non-sterile
              </ToggleButton>
            )}
          </ToggleButtonGroup>

          <Chip
            icon={<InfoOutlinedIcon fontSize="small" />}
            label="See examples — Sterile / Non-sterile"
            variant="outlined"
            onClick={(e) => setInfoAnchor(e.currentTarget)}
            sx={{ borderRadius: 999, fontWeight: 500 }}
          />

          <Autocomplete
            size="small"
            sx={{ minWidth: 260 }}
            options={searchOptions}
            value={selectedStateOption}
            isOptionEqualToValue={(opt, val) =>
              opt.kind === val.kind &&
              (opt.kind === "state" ? opt.code === (val as Extract<SearchOption, { kind: "state" }>).code : opt.id === (val as Extract<SearchOption, { kind: "pharmacy" }>).id)
            }
            groupBy={(opt) => (opt.kind === "state" ? "States" : "Pharmacies")}
            getOptionLabel={(opt) => opt.label}
            onChange={(_, opt) => {
              if (!opt) {
                clearSelectedState();
                return;
              }
              if (opt.kind === "state") {
                setSelected(opt.code);
                setDetailAnchor(null);
              } else if (!isProductScoped) {
                setFilter(opt.id);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={isProductScoped ? "Search state..." : "Search state or pharmacy..."}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchRoundedIcon fontSize="small" sx={{ color: selected ? "#0d9488" : "text.secondary", ml: 0.5, mr: -0.25 }} />,
                  sx: {
                    borderRadius: 999,
                    ...(selected
                      ? {
                          bgcolor: isDark ? alpha("#14b8a6", 0.16) : alpha("#0d9488", 0.1),
                          "& fieldset": { borderColor: "#0d9488", borderWidth: 1.5 },
                          "&:hover fieldset": { borderColor: "#0f766e" },
                          "&.Mui-focused fieldset": { borderColor: "#0d9488" },
                        }
                      : {}),
                  },
                }}
              />
            )}
          />
          {selected && (
            <Chip
              data-testid="selected-state-chip"
              label={`${STATE_NAMES[selected]}  ${selected}`}
              onDelete={clearSelectedState}
              sx={{
                borderRadius: 999,
                fontWeight: 600,
                bgcolor: isDark ? alpha("#14b8a6", 0.2) : alpha("#0d9488", 0.12),
                color: isDark ? "#5eead4" : "#0f766e",
                border: 1,
                borderColor: alpha("#0d9488", 0.35),
                "& .MuiChip-deleteIcon": { color: isDark ? "#5eead4" : "#0f766e" },
              }}
            />
          )}

          <Box sx={{ flexGrow: 1 }} />
          <Chip
            icon={<PublicOutlinedIcon fontSize="small" sx={{ color: `${colors.success[300]} !important` }} />}
            label={
              coverageLoading
                ? `Loading ${typeLabel.toLowerCase()} ship-to coverage…`
                : isNonSterileComingSoon
                  ? "Non-sterile ship-to coverage — Coming soon"
                  : `Confirmed ${typeLabel.toLowerCase()} ship-to — ${coveredCount} of ${TOTAL_JURISDICTIONS} jurisdictions covered`
            }
            sx={{
              fontWeight: 600,
              borderRadius: 999,
              bgcolor: isDark ? "rgba(34,197,94,0.12)" : colors.success[500],
              color: colors.success[100],
            }}
          />
        </Stack>
        {coverageLoading && <LinearProgress sx={{ mt: 1.25, borderRadius: 999 }} />}
        {coverageError && (
          <Typography variant="body2" sx={{ color: "error.main", mt: 1 }}>
            {coverageError}
          </Typography>
        )}
      </Paper>

      <Popover
        open={Boolean(infoAnchor)}
        anchorEl={infoAnchor}
        onClose={() => setInfoAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { borderRadius: 2.5, boxShadow: "0 12px 32px rgba(15,23,42,0.18)" } } }}
      >
        <Box sx={{ p: 2, maxWidth: 340 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Sterile vs. Non-sterile
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            <b>Sterile</b> compounds (e.g. injectables) require a cleanroom-certified pharmacy operating
            under USP &lt;797&gt;/&lt;800&gt;.
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <b>Non-sterile</b> compounds (e.g. oral, topical) are prepared under USP &lt;795&gt; and don't
            require cleanroom certification.
          </Typography>
        </Box>
      </Popover>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}>
        {!isProductScoped && (
          <Chip
            label="All pharmacies"
            onClick={() => setFilter("all")}
            sx={{
              fontWeight: 600,
              borderRadius: 999,
              transition: "all 150ms ease",
              cursor: "pointer",
              "&:hover": { transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(13,148,136,0.25)" },
              ...(filter === "all"
                ? { background: BRAND_GRADIENT, color: "#fff", boxShadow: "0 2px 8px rgba(13,148,136,0.3)" }
                : { bgcolor: "transparent", color: "text.primary", border: 1, borderColor: "divider" }),
            }}
          />
        )}
        {pharmacies.map((p) => {
          const active = filter === p.id;
          const color = pharmacyColor(p.id);
          return (
            <Chip
              key={p.id}
                    onClick={() => {
                      if (!isProductScoped) setFilter(p.id);
                    }}
              icon={
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: color,
                    ml: "10px !important",
                  }}
                />
              }
              label={`${p.name}  ${pharmacyStateCount(p.id, compoundType, coverage)}`}
              sx={{
                fontWeight: 500,
                borderRadius: 999,
                border: 1,
                cursor: "pointer",
                borderColor: active ? color : "divider",
                bgcolor: active ? `${color}1f` : "transparent",
                color: active ? color : "text.primary",
                transition: "all 150ms ease",
                "&:hover": { transform: "translateY(-1px)", boxShadow: `0 4px 12px ${alpha(color, 0.25)}` },
              }}
            />
          );
        })}
      </Stack>

      <Paper
        data-testid="coverage-overview"
        elevation={0}
        sx={{
          ...cardSx,
          mb: 2,
          p: 2,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Box sx={{ minWidth: 180 }}>
          <Typography variant="caption" sx={{ color: "#0d9488", fontWeight: 700, letterSpacing: 0.6 }}>
            {typeLabel.toUpperCase()} OVERVIEW
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {overviewPharmacies.length} pharmacy partner{overviewPharmacies.length === 1 ? "" : "s"} loaded
            {isProductScoped ? " for this product" : ""}
          </Typography>
        </Box>

        <Stack direction="row" spacing={3} flexWrap="wrap" rowGap={1.5}>
          {overviewPharmacies.map((p) => {
            const count = pharmacyStateCount(p.id, compoundType, coverage);
            const color = pharmacyColor(p.id);
            return (
              <Box key={p.id} sx={{ minWidth: 150 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {p.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {count}/{TOTAL_JURISDICTIONS}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(count / TOTAL_JURISDICTIONS) * 100}
                  sx={{
                    height: 5,
                    borderRadius: 999,
                    bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)",
                    "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 999 },
                  }}
                />
              </Box>
            );
          })}
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        <Stack alignItems={{ xs: "flex-start", md: "flex-end" }} spacing={0.5}>
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon fontSize="small" sx={{ color: "#0d9488 !important" }} />}
            label={
              isNonSterileComingSoon
                ? "Non-sterile coverage is coming soon."
                : `${coveredCount} of ${TOTAL_JURISDICTIONS} jurisdictions have at least one ${compoundType === "sterile" ? "sterile" : "non-sterile"} pharmacy.`
            }
            sx={{
              bgcolor: isDark ? alpha("#14b8a6", 0.18) : "#ffffff",
              color: "#0d9488",
              fontWeight: 600,
              border: 1,
              borderColor: alpha("#0d9488", 0.25),
            }}
          />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {isNonSterileComingSoon ? "Map coverage will appear here when non-sterile products are available." : "Click a state on the map for details."}
          </Typography>
        </Stack>
      </Paper>

      <Paper
        ref={containerRef}
        elevation={0}
        sx={{
          ...cardSx,
          position: "relative",
          p: 1,
          overflow: "hidden",
          ...(isFullscreen && {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
            p: 3,
          }),
        }}
        onMouseMove={(e) => {
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) setPointer({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        }}
      >
        <ComposableMap
          projection="geoAlbersUsa"
          width={MAP_WIDTH}
          height={MAP_HEIGHT}
          style={{
            width: "100%",
            height: "auto",
            ...(isNonSterileComingSoon ? { filter: "grayscale(0.45) blur(1.5px)", opacity: 0.45 } : {}),
          }}
        >
          <ZoomableGroup
            center={mapPosition.coordinates}
            zoom={mapPosition.zoom}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            onMoveEnd={setMapPosition}
            filterZoomEvent={(event: any) => event.type !== "wheel"}
          >
            <Geographies geography={US_TOPO_URL}>
              {({ geographies }: { geographies: any[] }) => (
                <>
                  {geographies.map((geo) => {
                    const fips = String(geo.id).padStart(2, "0");
                    const code = FIPS_TO_CODE[fips];
                    if (!code) return null;
                    const isSel = selected === code;
                    const isHov = hovered === code;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onMouseEnter={() => setHovered(code)}
                        onMouseLeave={() => setHovered(null)}
                        onClick={(evt: React.MouseEvent) => handleStateClick(code, evt)}
                        style={{
                          default: {
                            fill: isSel
                              ? (byState[code]?.length ? "#14b8a6" : (isDark ? colors.grey[500] : colors.grey[500]))
                              : fillFor(code),
                            stroke: isSel ? selectedStroke : strokeColor,
                            strokeWidth: isSel ? 2.2 : 0.75,
                            outline: "none",
                            cursor: "pointer",
                            vectorEffect: "non-scaling-stroke",
                            transition: "fill 120ms ease, stroke-width 120ms ease",
                          },
                          hover: {
                            fill: byState[code]?.length ? hoverFill : (isDark ? colors.grey[600] : colors.grey[600]),
                            stroke: isSel ? selectedStroke : strokeColor,
                            strokeWidth: isSel ? 2.2 : 0.75,
                            outline: "none",
                            cursor: "pointer",
                            vectorEffect: "non-scaling-stroke",
                          },
                          pressed: {
                            fill: colors.primary[200],
                            stroke: strokeColor,
                            outline: "none",
                            vectorEffect: "non-scaling-stroke",
                          },
                        }}
                        {...(isSel || isHov
                          ? { stroke: selectedStroke, strokeWidth: isSel ? 2.2 : 1.1 }
                          : {})}
                      />
                    );
                  })}

                  {geographies.map((geo) => {
                    const fips = String(geo.id).padStart(2, "0");
                    const code = FIPS_TO_CODE[fips];
                    if (!code) return null;
                    const centroid = geoCentroid(geo);
                    const n = byState[code]?.length ?? 0;
                    if (!centroid || Number.isNaN(centroid[0])) return null;
                    return (
                      <Marker key={`m-${geo.rsmKey}`} coordinates={centroid as [number, number]}>
                        <g transform={`scale(${1 / mapPosition.zoom})`}>
                          <text
                            textAnchor="middle"
                            y={-1}
                            style={{
                              fontFamily: "inherit",
                              fontSize: 8,
                              fontWeight: 700,
                              pointerEvents: "none",
                              fill: n > 0 ? "#ffffff" : (isDark ? colors.grey[500] : colors.grey[500]),
                            }}
                          >
                            {code}
                          </text>
                          {filter === "all" && n > 0 && (
                            <g transform="translate(9, -9)">
                              <circle r={6.5} fill={badgeColor} stroke={strokeColor} strokeWidth={0.75} />
                              <text
                                textAnchor="middle"
                                y={2.2}
                                style={{
                                  fontFamily: "inherit",
                                  fontSize: 7,
                                  fontWeight: 700,
                                  pointerEvents: "none",
                                  fill: "#ffffff",
                                }}
                              >
                                {n}
                              </text>
                            </g>
                          )}
                        </g>
                      </Marker>
                    );
                  })}
                </>
              )}
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>

        {isNonSterileComingSoon && (
          <Box
            data-testid="nonsterile-coming-soon"
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.background.default, isDark ? 0.45 : 0.35),
              backdropFilter: "blur(4px)",
              pointerEvents: "auto",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.2,
                color: "text.primary",
                textAlign: "center",
                px: 2,
              }}
            >
              Non-sterile coverage coming soon
            </Typography>
          </Box>
        )}

        <Stack
          direction="row"
          spacing={1.75}
          alignItems="center"
          sx={{
            position: "absolute",
            left: 12,
            top: 12,
            p: 1,
            borderRadius: 3,
            bgcolor: isDark ? "rgba(20,30,40,0.55)" : "rgba(255,255,255,0.75)",
            backdropFilter: "blur(10px)",
            border: 1,
            borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
            zIndex: 4,
            flexWrap: "wrap",
            rowGap: 0.5,
          }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: coveredFill }} />
            <Typography variant="caption">Ships {typeLabel.toLowerCase()} compounds</Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: uncoveredFill }} />
            <Typography variant="caption">No coverage</Typography>
          </Stack>
          {filter === "all" && (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  bgcolor: badgeColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontSize: 8, fontWeight: 700, color: "#fff" }}>N</Typography>
              </Box>
              <Typography variant="caption">Partners covering that state</Typography>
            </Stack>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            p: 0.5,
            borderRadius: 3,
            bgcolor: isDark ? "rgba(20,30,40,0.55)" : "rgba(255,255,255,0.75)",
            backdropFilter: "blur(10px)",
            border: 1,
            borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
            boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
            zIndex: 4,
          }}
        >
          <IconButton size="small" onClick={zoomIn} disabled={mapPosition.zoom >= MAX_ZOOM} title="Zoom in">
            <ZoomInIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={zoomOut} disabled={mapPosition.zoom <= MIN_ZOOM} title="Zoom out">
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={resetZoom} title="Reset view">
            <RestartAltIcon fontSize="small" />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
          <IconButton size="small" onClick={exportMapImage} title="Download map as PNG">
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={toggleFullscreen} title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
            {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
          </IconButton>
        </Stack>

        {hovered && (
          <Box
            sx={{
              position: "absolute",
              left: pointer.x + 14,
              top: pointer.y + 14,
              px: 1.25,
              py: 0.75,
              borderRadius: 1.5,
              pointerEvents: "none",
              bgcolor: "grey.900",
              color: "#fff",
              boxShadow: 3,
              maxWidth: 220,
              zIndex: 5,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
              {STATE_NAMES[hovered]}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {byState[hovered]?.length
                ? `${byState[hovered].length} partner${byState[hovered].length > 1 ? "s" : ""}`
                : "No coverage"}
            </Typography>
          </Box>
        )}
      </Paper>

      <Popover
        open={Boolean(selected && detailAnchor)}
        onClose={() => setDetailAnchor(null)}
        anchorReference="anchorPosition"
        anchorPosition={detailAnchor ? { top: detailAnchor.y, left: detailAnchor.x } : undefined}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { borderRadius: 2.5, boxShadow: "0 12px 32px rgba(15,23,42,0.18)", overflow: "hidden" } } }}
      >
        {selected && (
          <Box sx={{ minWidth: 240, maxWidth: 300 }}>
            <Box sx={{ p: 2 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {STATE_NAMES[selected]}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {typeLabel} compounds
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setDetailAnchor(null)} sx={{ mt: -0.5, mr: -0.5 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Divider sx={{ my: 1.25 }} />
              {selectedPartners.length ? (
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {selectedPartners.length} partner{selectedPartners.length > 1 ? "s" : ""} ship here:
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.75}>
                    {selectedPartners.map((name) => {
                      const ph = pharmacies.find((p) => p.name === name);
                      const color = ph ? pharmacyColor(ph.id) : colors.primary[300];
                      return (
                        <Chip
                          key={name}
                          label={name}
                          variant="outlined"
                          size="small"
                          sx={{ borderColor: color, color: "text.primary" }}
                        />
                      );
                    })}
                  </Stack>
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No partner currently ships {typeLabel.toLowerCase()} compounds to this state.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
}
